import { FC, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Button, Card, FormHelperText, Stack, Typography } from '@mui/material';

import { ArrowBack, ArrowForward } from '@mui/icons-material';

import { useRouter } from '@cvt/routing';

import MediaDropzone from '@modules/Tributes/components/MediaDropzone';
import { TributeFormData } from '../CreateTribute/CreateTribute';


interface Props {
  onComplete: () => void;
}

export const MediaUploadStep: FC<Props> = (props) => {
  const { onComplete } = props;

  const router = useRouter();
  const { getValues, setValue, control, handleSubmit } = useFormContext<TributeFormData>();

  const addFiles = useCallback((newFiles: File[]) => {
    const currentFiles = getValues('mediaFiles') || [];

    setValue('mediaFiles', [...currentFiles, ...newFiles], {
      shouldValidate: true,
      shouldDirty: true,
    });
    const mainPhoto = getValues('mainPhoto');
    if (!mainPhoto && newFiles.length > 0) {
      const firstImageFile = newFiles.find(file => file.type.startsWith('image/'));
      if (firstImageFile) {
        setValue('mainPhoto', firstImageFile, { shouldValidate: true });
      }
    }
  }, [setValue, getValues]);

  const removeFile = useCallback((file: File) => {
    const currentFiles = getValues('mediaFiles') || [];
    setValue('mediaFiles', currentFiles.filter(f => f !== file), {
      shouldValidate: true,
      shouldDirty: true,
    });
    const mainPhoto = getValues('mainPhoto');
    if (mainPhoto === file) {
      setValue('mainPhoto', null, { shouldValidate: true });
    }
  }, [setValue, getValues]);

  const onNext = useCallback(() => {
    onComplete();
  }, [onComplete]);

  const onPrevious = useCallback(() => {
    router.tributes.create.details.go();
  }, [router]);

  return (
    <form style={{ width: '100%', height: '100%' }} onSubmit={handleSubmit(onNext)}>
      <Stack maxWidth="sm" margin="0 auto" sx={{ py: 2, px: { xs: 2, sm: 0 } }}>
        <Card sx={{
          boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgb(254, 238, 237)',
          borderRadius: '12px',
          overflow: 'visible',
          p: 4,
        }}
        >
          <Stack gap={2}>
            <Stack
              bgcolor="primary.main"
              gap={1}
              sx={{
                px: 3,
                py: 3,
                borderRadius: '12px 12px 0 0',
                background: 'primary.main',
              }}
            >
              <Typography variant="body1" fontSize={16} fontWeight={800} textAlign="center" color="white">
                Upload Photos and Videos
              </Typography>
              <Typography variant="body1" color="white" fontSize={14} fontWeight={500} textAlign="center">
                Start building your LGCY timeline by selecting a few cherished moments. To start your Journey of Love
                you must upload at least 1 photo to start.
              </Typography>
            </Stack>
            <Controller
              name="mediaFiles"
              control={control}
              rules={{
                validate: (value) =>
                  value.length >= 1 || 'Please upload at least 1 photo to start your Journey of Love',
              }}
              render={({ field, fieldState }) => (
                <>
                  <MediaDropzone
                    initialFiles={field.value}
                    onFilesAdded={addFiles}
                    onFileRemoved={removeFile}
                  />
                  {fieldState.error && (
                    <FormHelperText error>
                      {fieldState.error.message}
                    </FormHelperText>
                  )}
                </>
              )}
            />

          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mt={4}
          >
            <Button
              variant="outlined"
              color="default"
              onClick={onPrevious}
              sx={{
                px: 3,
                py: 1,
              }}
              startIcon={<ArrowBack/>}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                px: 3,
                py: 1,
              }}
              endIcon={<ArrowForward/>}
            >
              Next
            </Button>
          </Stack>
        </Card>
      </Stack>
    </form>
  );
};
